import React, { useState } from 'react'

const ProductSelector = ({ products, selectedProducts, onProductChange, loading }) => {
  const [mainProductVariants, setMainProductVariants] = useState({
    'Product A': 'Standard',
    'Product B': 'Standard',
    'Product C': 'Standard'
  })

  const mainProducts = products.filter(p => p.category === 'Main')
  const externalProducts = products.filter(p => p.category === 'External')

  const handleMainProductChange = (productName, isChecked) => {
    const variant = mainProductVariants[productName]
    // Convert 'Product A' to 'prod_a_std' or 'prod_a_ent'
    const productLetter = productName.split(' ')[1].toLowerCase()
    const variantSuffix = variant.toLowerCase() === 'standard' ? 'std' : 'ent'
    const productId = `prod_${productLetter}_${variantSuffix}`
    
    if (isChecked) {
      onProductChange([...selectedProducts, productId])
    } else {
      // Remove both variants when unchecking
      const filtered = selectedProducts.filter(id => 
        !id.startsWith(`prod_${productLetter}_`)
      )
      onProductChange(filtered)
    }
  }

  const handleVariantChange = (productName, newVariant) => {
    const oldVariant = mainProductVariants[productName]
    // Convert 'Product A' to 'prod_a_std' or 'prod_a_ent'
    const productLetter = productName.split(' ')[1].toLowerCase()
    const oldVariantSuffix = oldVariant.toLowerCase() === 'standard' ? 'std' : 'ent'
    const newVariantSuffix = newVariant.toLowerCase() === 'standard' ? 'std' : 'ent'
    const oldId = `prod_${productLetter}_${oldVariantSuffix}`
    const newId = `prod_${productLetter}_${newVariantSuffix}`
    
    setMainProductVariants({
      ...mainProductVariants,
      [productName]: newVariant
    })

    // Update selected products if this product is selected
    if (selectedProducts.includes(oldId)) {
      const updated = selectedProducts.map(id => id === oldId ? newId : id)
      onProductChange(updated)
    }
  }

  const handleExternalProductChange = (productId, isChecked) => {
    if (isChecked) {
      onProductChange([...selectedProducts, productId])
    } else {
      onProductChange(selectedProducts.filter(id => id !== productId))
    }
  }

  const getUniqueMainProducts = () => {
    const unique = {}
    mainProducts.forEach(p => {
      if (!unique[p.name]) {
        unique[p.name] = p
      }
    })
    return Object.values(unique)
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading products...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Product Selection</h2>
      
      {/* Main Products */}
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">Main Products</h3>
        <div className="space-y-4">
          {getUniqueMainProducts().map((product) => {
            const variant = mainProductVariants[product.name]
            // Convert 'Product A' to 'prod_a_std' or 'prod_a_ent'
            const productLetter = product.name.split(' ')[1].toLowerCase()
            const variantSuffix = variant.toLowerCase() === 'standard' ? 'std' : 'ent'
            const productId = `prod_${productLetter}_${variantSuffix}`
            const isSelected = selectedProducts.includes(productId)
            const selectedProduct = products.find(p => p.id === productId)
            
            return (
              <div key={product.name} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id={product.name}
                      checked={isSelected}
                      onChange={(e) => handleMainProductChange(product.name, e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor={product.name} className="ml-3 cursor-pointer">
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{selectedProduct?.description}</div>
                    </label>
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* Variant Toggle */}
                    <div className="flex rounded-md shadow-sm">
                      <button
                        type="button"
                        onClick={() => handleVariantChange(product.name, 'Standard')}
                        className={`px-3 py-1 text-sm font-medium rounded-l-md border ${
                          variant === 'Standard'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Standard
                      </button>
                      <button
                        type="button"
                        onClick={() => handleVariantChange(product.name, 'Enterprise')}
                        className={`px-3 py-1 text-sm font-medium rounded-r-md border-t border-b border-r ${
                          variant === 'Enterprise'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Enterprise
                      </button>
                    </div>
                    {/* Price */}
                    <div className="text-lg font-semibold text-gray-900 min-w-[100px] text-right">
                      €{selectedProduct?.price || 0}/mo
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* External Services */}
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">Additional Services</h3>
        <div className="grid grid-cols-2 gap-4">
          {externalProducts.map((product) => (
            <div key={product.id} className="border rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id={product.id}
                  checked={selectedProducts.includes(product.id)}
                  onChange={(e) => handleExternalProductChange(product.id, e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor={product.id} className="ml-3 flex-1 cursor-pointer">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{product.description}</div>
                    </div>
                    <div className="text-lg font-semibold text-gray-900 ml-4">
                      €{product.price}/mo
                    </div>
                  </div>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bundle Discount Notice */}
      {selectedProducts.length > 1 && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-green-800 font-medium">
              Bundle Discount Applied! Save 10% when selecting multiple products.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductSelector